/**
 * Build an English-language monthly employee work schedule without an Excel template.
 * Intended for React web apps.
 *
 * Install:
 *   npm install xlsx-js-style moment numeral
 *
 * groupedData format:
 * {
 *   "6328": {
 *     employee_id: 6328,
 *     employee_name: "Employee Name",
 *     employee_skill: "Position",
 *     hourly_rate: "42.25",
 *     events: [{ start, end, worked_hours, night_hours, total_hours }]
 *   }
 * }
 */
import * as XLSX from 'xlsx-js-style';
import moment from 'moment';
import numeral from 'numeral';

const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
const FIRST_EMPLOYEE_ROW = 6; // 1-based Excel row
const ROWS_PER_EMPLOYEE = 4; // Shift, Day, Night, Total
const FIRST_DAY_COL = 5; // 0-based F
const WEEKDAY_ROW = 4;
const DATE_ROW = 5;
const TITLE_ROW = 1;
const COMPANY_ROW = 2;
const MONTH_ROW = 3;

const DEFAULT_WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const COLORS = {
  charcoal: '404040',
  darkGray: '595959',
  mediumGray: 'D9D9D9',
  lightGray: 'F2F2F2',
  lightBlue: 'DDEBF7',
  white: 'FFFFFF',
  text: '222222',
  border: 'A6A6A6',
};

const thinBorder = {
  top: { style: 'thin', color: COLORS.border },
  bottom: { style: 'thin', color: COLORS.border },
  left: { style: 'thin', color: COLORS.border },
  right: { style: 'thin', color: COLORS.border },
};

// The decimal separator is optional, so whole counts render as "8" rather than "8,".
// Counts are written as formatted strings below, not as numeric Excel cells.
const HOUR_NUMERAL_FORMAT = '0[.]0';
const EXCEL_RATE_NUM_FORMAT = '0.00';
const SUNDAY_SEPARATOR_BORDER = { style: 'medium', color: COLORS.darkGray };
const TABLE_OUTLINE_BORDER = {
  style: 'thick',
  color: COLORS.darkGray,
};
const SECTION_BORDER = {
  style: 'medium',
  color: COLORS.darkGray,
};

const styles = {
  // Default for every used cell, including otherwise-empty cells.
  blank: {
    font: { name: 'Arial', sz: 9, color: { rgb: COLORS.text } },
    alignment: { vertical: 'center' },
    border: thinBorder,
  },
  title: {
    font: { name: 'Arial', sz: 16, bold: true },
    alignment: { horizontal: 'center', vertical: 'center' },
  },
  company: {
    font: { name: 'Arial', sz: 11, bold: true },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  },
  companyDescription: {
    font: { name: 'Arial', sz: 10, bold: false },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  },
  month: {
    font: { name: 'Arial', sz: 12, bold: true },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  },
  // All column headers have no fill unless specifically called out below.
  header: {
    font: {
      name: 'Arial', sz: 9, bold: true, color: { rgb: COLORS.text },
    },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: thinBorder,
  },
  // Gray fill is used only for weekday-name cells.
  weekday: {
    font: {
      name: 'Arial', sz: 9, bold: true, color: { rgb: COLORS.text },
    },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: thinBorder,
  },
  weekend: {
    font: {
      name: 'Arial', sz: 9, bold: true, color: { rgb: COLORS.text },
    },
    fill: { patternType: 'solid', fgColor: { rgb: COLORS.mediumGray } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: thinBorder,
  },
  date: {
    font: {
      name: 'Arial', sz: 9, bold: true, color: { rgb: COLORS.text },
    },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: thinBorder,
    numFmt: '0',
  },
  label: {
    font: {
      name: 'Arial', sz: 9, bold: true, color: { rgb: COLORS.text },
    },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: thinBorder,
  },
  totalLabel: {
    font: {
      name: 'Arial', sz: 9, bold: true, color: { rgb: COLORS.text },
    },
    fill: { patternType: 'solid', fgColor: { rgb: COLORS.lightBlue } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: thinBorder,
  },
  name: {
    font: { name: 'Arial', sz: 9, color: { rgb: COLORS.text } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: false },
    border: thinBorder,
  },
  body: {
    font: { name: 'Arial', sz: 9, color: { rgb: COLORS.text } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: false },
    border: thinBorder,
  },
  hourlyRate: {
    font: { name: 'Arial', sz: 9, color: { rgb: COLORS.text } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: thinBorder,
    numFmt: EXCEL_RATE_NUM_FORMAT,
  },
  total: {
    font: {
      name: 'Arial', sz: 9, bold: true, color: { rgb: COLORS.text },
    },
    fill: { patternType: 'solid', fgColor: { rgb: COLORS.lightBlue } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: thinBorder,
  },
  totalMonth: {
    font: {
      name: 'Arial', sz: 9, bold: true, color: { rgb: COLORS.text },
    },
    fill: { patternType: 'solid', fgColor: { rgb: COLORS.mediumGray } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: thinBorder,
  },
  signature: {
    font: { name: 'Arial', sz: 9, color: { rgb: COLORS.text } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: thinBorder,
  },
  description: {
    font: { name: 'Arial', sz: 9, color: { rgb: COLORS.text } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: thinBorder,
  },
  borderless: {
    font: { name: 'Arial', sz: 9, color: { rgb: COLORS.text } },
    alignment: { vertical: 'center' },
  },
};

function autoFitColumn(
  ws,
  colIndex,
  { minWidth = 3, maxWidth = 40, padding = 2 } = {},
) {
  if (!ws['!ref']) return;

  const range = XLSX.utils.decode_range(ws['!ref']);
  const mergedRanges = ws['!merges'] || [];
  let maxLength = 0;

  for (let row = range.s.r; row <= range.e.r; row += 1) {
    // Ignore horizontally merged cells so their long text
    // doesn't make an individual column excessively wide.
    const isMergedAcrossColumns = mergedRanges.some(
      ({ s, e }) =>
        s.r === row && s.c === colIndex && e.c > s.c,
    );

    if (isMergedAcrossColumns) continue;

    const address = XLSX.utils.encode_cell({
      r: row,
      c: colIndex,
    });

    const cell = ws[address];
    if (!cell || cell.v == null) continue;

    const lines = String(cell.v).split(/\r?\n/);

    for (const line of lines) {
      maxLength = Math.max(maxLength, line.length*0.7);
    }
  }

  ws['!cols'][colIndex] = {
    ...ws['!cols'][colIndex],
    wch: Math.max(
      minWidth,
      Math.min(maxWidth, maxLength + padding),
    ),
  };
}

function cellAddress(row1Based, col0Based) {
  return XLSX.utils.encode_cell({ r: row1Based - 1, c: col0Based });
}

function normalizeCell(cell) {
  if (!cell || cell.t === 'z' || cell.v === undefined) {
    return { t: 's', v: ' ' };
  }

  return cell;
}

function setCell(ws, row1Based, col0Based, value, style) {
  const address = cellAddress(row1Based, col0Based);
  const cell = {};
  const sheet = ws;

  if (value === undefined || value === null || value === '') {
    // xlsx-js-style may omit styled blank/stub cells on export. A single-space
    // string forces the cell to be serialized so its border remains visible.
    cell.t = 's';
    cell.v = ' ';
  } else if (typeof value === 'number' && Number.isFinite(value)) {
    cell.t = 'n';
    cell.v = value;
  } else {
    cell.t = 's';
    cell.v = String(value);
  }

  cell.s = style || styles.blank;
  sheet[address] = cell;
}

function applyCellStyle(ws, row1Based, col0Based, style) {
  const address = cellAddress(row1Based, col0Based);
  const sheet = ws;
  const existing = normalizeCell(sheet[address]);
  existing.s = style;
  sheet[address] = existing;
}

function applyRightBorder(ws, row1Based, col0Based, border) {
  const address = cellAddress(row1Based, col0Based);
  const sheet = ws;
  const cell = normalizeCell(sheet[address]);

  const currentStyle = cell.s || styles.blank;
  cell.s = {
    ...currentStyle,
    border: {
      ...(currentStyle.border || thinBorder),
      right: { ...border },
    },
  };
  sheet[address] = cell;
}

function applyBorderSides(ws, row1Based, col0Based, sides, border) {
  const address = cellAddress(row1Based, col0Based);
  const sheet = ws;
  const cell = normalizeCell(sheet[address]);

  const currentStyle = cell.s || styles.blank;
  const updatedBorder = { ...(currentStyle.border || thinBorder) };

  sides.forEach((side) => {
    updatedBorder[side] = { ...border };
  });

  cell.s = {
    ...currentStyle,
    border: updatedBorder,
  };

  sheet[address] = cell;
}

function applyRangeStyle(ws, startRow, endRow, col, style) {
  for (let row = startRow; row <= endRow; row += 1) {
    applyCellStyle(ws, row, col, style);
  }
}

function applyRowBorder(ws, row, startCol, endCol, borderType, border) {
  for (let col = startCol; col <= endCol; col += 1) {
    applyBorderSides(ws, row, col, borderType, border);
  }
}

function parseDateTime(value, fieldName, employeeId) {
  const parsed = moment(value, DATETIME_FORMAT, true);
  if (!parsed.isValid()) {
    throw new Error(
      `Invalid ${fieldName} value for employee ${employeeId}: "${value}". Expected ${DATETIME_FORMAT}.`,
    );
  }
  return parsed;
}

function toHourNumber(value, fieldName, employeeId) {
  if (value === undefined || value === null || value === '') return 0;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    throw new TypeError(`Invalid ${fieldName} value for employee ${employeeId}: "${value}".`);
  }

  // Normalize to one decimal place at most while keeping the value numeric for
  // grouping/summing in JavaScript. The exported count cells are strings.
  const formatted = numeral(numericValue).format(HOUR_NUMERAL_FORMAT);
  const rounded = numeral(formatted).value();
  if (!Number.isFinite(rounded)) {
    throw new TypeError(`Could not format ${fieldName} value for employee ${employeeId}: "${value}".`);
  }
  return rounded;
}

function formatHourCount(value) {
  if (value === undefined || value === null || value === '') return '';

  // Using an optional decimal separator avoids a trailing comma/dot for integers.
  // Strip a trailing separator defensively in case a customized Numeral locale or
  // format registration returns one anyway. The result is stored as a string cell.
  return numeral(value).format(HOUR_NUMERAL_FORMAT).replace(/[.,]$/, '');
}

function setCountCell(ws, row1Based, col0Based, value, style) {
  const formattedValue = formatHourCount(value);
  setCell(ws, row1Based, col0Based, formattedValue, style);
}

function toHourlyRate(value, employeeId) {
  if (value === undefined || value === null || value === '') return '';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    throw new TypeError(`Invalid hourly_rate value for employee ${employeeId}: "${value}".`);
  }
  return numericValue;
}

function formatShift(event, employeeId) {
  const start = parseDateTime(event.start, 'start', employeeId);
  const end = parseDateTime(event.end, 'end', employeeId);
  const endLabel = end.format('HH:mm:ss') === '23:59:59' ? '24:00' : end.format('HH:mm');
  return { start, text: `${start.format('HH:mm')}-${endLabel}` };
}

const ROLE_TRANSLATIONS = {
  'padavėja (-as)': 'Server',
  'administratorė (-as)': 'Administrator',
  'barmenas (-ė)': 'Bartender',
  'restorano pagalbinis darbuotojas (-a)': 'Restaurant Assistant',
};

function translateEmployeeSkill(skill) {
  const original = String(skill || '').trim();
  return ROLE_TRANSLATIONS[original.toLowerCase()] || original;
}

function getEmployees(groupedData) {
  if (!groupedData || typeof groupedData !== 'object' || Array.isArray(groupedData)) {
    throw new TypeError('groupedData must be an object keyed by employee ID.');
  }

  return Object.entries(groupedData)
    .filter(([, employee]) => employee && typeof employee === 'object' && !Array.isArray(employee))
    .map(([id, employee]) => ({
      id,
      name: employee.employee_name || '',
      skill: translateEmployeeSkill(employee.employee_skill || ''),
      hourlyRate: employee.hourly_rate,
      events: Array.isArray(employee.events) ? employee.events : [],
    }));
}

function resolveWeekdayLabels(weekdayLabels) {
  if (weekdayLabels == null) return DEFAULT_WEEKDAY_LABELS;
  if (
    !Array.isArray(weekdayLabels)
    || weekdayLabels.length !== 7
    || weekdayLabels.some((label) => typeof label !== 'string' || !label.trim())
  ) {
    throw new TypeError(
      'weekdayLabels must be an array of 7 non-empty strings ordered Sunday through Saturday.',
    );
  }
  return weekdayLabels;
}

function resolveYearMonth(employees, year, month) {
  const sampleStart = employees
    .map(({ events }) => events.find((item) => item && item.start))
    .map((event) => (event ? moment(event.start, DATETIME_FORMAT, true) : null))
    .find((value) => value && value.isValid());

  if ((year == null || month == null) && !sampleStart) {
    throw new Error(
      'Pass year and month, or provide at least one valid event start so the month can be inferred.',
    );
  }

  const resolvedYear = Number(year == null ? sampleStart.year() : year);
  const resolvedMonth = Number(month == null ? sampleStart.month() + 1 : month);
  if (!Number.isInteger(resolvedYear) || resolvedYear < 1900 || resolvedYear > 9999) {
    throw new RangeError(`Invalid year: ${year}`);
  }
  if (!Number.isInteger(resolvedMonth) || resolvedMonth < 1 || resolvedMonth > 12) {
    throw new RangeError(`Invalid month: ${month}. Use 1-12.`);
  }
  return { year: resolvedYear, month: resolvedMonth };
}

function setMerge(ws, range) {
  const sheet = ws;
  sheet['!merges'] = sheet['!merges'] || [];
  sheet['!merges'].push(XLSX.utils.decode_range(range));
}

/**
 * Creates a formatted schedule workbook from precomputed event-hour values.
 * No hours are calculated from start/end times; worked_hours, night_hours,
 * and total_hours are read directly from each event in groupedData.
 *
 * @param {object} options
 * @param {object} options.groupedData Object keyed by employee ID; each value has employee fields and an events array
 * @param {number} [options.year] Four-digit year; inferred from event dates if omitted
 * @param {number} [options.month] Month 1-12; inferred from event dates if omitted
 * @param {string} [options.companyName] Company name shown below the title
 * @param {string} [options.description] Optional full-width bordered description shown below the table
 * @param {string} [options.monthTitle] Optional custom month heading
 * @param {string[]} [options.weekdayLabels] Weekday labels indexed Sunday=0 through Saturday=6
 * @param {string} [options.title] Main schedule title
 * @param {string} [options.sheetName] Worksheet name
 * @returns {{
 *   workbook: object,
 *   sheetName: string,
 *   employeeCount: number,
 *   year: number,
 *   month: number,
 *   daysInMonth: number,
 * }}
 */
export default function createScheduleWorkbook(
  {
    groupedData,
    year,
    month,
    companyName = 'Company',
    title = 'WORK SCHEDULE',
    monthTitle,
    description,
    weekdayLabels,
    sheetName = 'Work Schedule',
  },
  t = (value) => value,
) {
  const employees = getEmployees(groupedData);
  const resolvedWeekdayLabels = resolveWeekdayLabels(weekdayLabels);
  const resolved = resolveYearMonth(employees, year, month);

  const monthStart = moment({
    year: resolved.year,
    month: resolved.month - 1,
    day: 1,
  }).startOf('day');

  const daysInMonth = monthStart.daysInMonth();

  const LAST_DAY_COL = FIRST_DAY_COL + daysInMonth - 1;

  // Summary columns immediately follow the final day.
  const TOTAL_COL = LAST_DAY_COL + 1;
  const HOLIDAY_COL = TOTAL_COL + 1;
  const SIGNATURE_COL = TOTAL_COL + 2;
  const LAST_COL = SIGNATURE_COL;

  const lastColumnLetter = XLSX.utils.encode_col(LAST_COL);
  const totalColumnLetter = XLSX.utils.encode_col(TOTAL_COL);
  const signatureColumnLetter = XLSX.utils.encode_col(SIGNATURE_COL);

  let lastRow = employees.length
    ? FIRST_EMPLOYEE_ROW - 1
      + employees.length * ROWS_PER_EMPLOYEE
    : DATE_ROW;

  const hasDescription = description !== undefined
    && description !== null
    && String(description).trim() !== '';

  const descriptionRow = hasDescription ? lastRow + 1 : null;
  const finalRow = descriptionRow || lastRow;

  const ws = XLSX.utils.aoa_to_sheet(
    Array.from(
      { length: finalRow },
      () => Array(LAST_COL + 1).fill(null),
    ),
  );

  ws['!merges'] = [];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  // Initialize every used cell with borders, including empty cells.
  for (let row = 1; row <= lastRow; row += 1) {
    for (let col = 0; col <= LAST_COL; col += 1) {
      applyCellStyle(
        ws,
        row,
        col,
        row <= 3 ? styles.borderless : styles.blank,
      );
    }
  }

  // Row 1 is kept as an empty, full-width row.
  setMerge(ws, `A${TITLE_ROW}:${lastColumnLetter}${TITLE_ROW}`);

  // Row 2: company name and title occupy separate merged ranges.
  setMerge(ws, 'A2:O2');
  setMerge(ws, 'R2:AA2');
  setMerge(ws, 'A3:O3');

  // Row 3: month heading spans the entire worksheet width.
  setMerge(ws, `R${MONTH_ROW}:AA${MONTH_ROW}`);

  // Style the blank first row.
  for (let col = 0; col <= LAST_COL; col += 1) {
    applyCellStyle(ws, TITLE_ROW, col, styles.borderless);
    applyCellStyle(ws, MONTH_ROW, col, styles.month);
  }

  // Company name: A2:O2.
  for (let col = 0; col <= 14; col += 1) {
    applyCellStyle(ws, COMPANY_ROW, col, styles.company);
  }

  setCell(
    ws,
    COMPANY_ROW,
    0,
    companyName,
    styles.company,
  );

  setCell(
    ws,
    MONTH_ROW,
    0,
    t('Company name/Department name'),
    styles.companyDescription,
  );

  // Title: R2:AA2.
  const TITLE_START_COL = 17; // R, zero-based
  const TITLE_END_COL = 26; // AA, zero-based

  for (
    let col = TITLE_START_COL;
    col <= TITLE_END_COL;
    col += 1
  ) {
    applyCellStyle(ws, COMPANY_ROW, col, styles.title);
  }

  setCell(
    ws,
    COMPANY_ROW,
    TITLE_START_COL,
    title,
    styles.title,
  );

  setCell(
    ws,
    MONTH_ROW,
    TITLE_START_COL,
    monthTitle,
    styles.month,
  );

  // Column headings.
  const fixedHeaders = [
    ['A4:A5', 0, t('No.')],
    ['B4:B5', 1, t('Employee Name')],
    ['C4:C5', 2, t('Position')],
    ['D4:D5', 3, t('Hourly rate')],
    ['E4:E5', 4, ''],
  ];

  fixedHeaders.forEach(([range, col, label]) => {
    setMerge(ws, range);

    setCell(
      ws,
      WEEKDAY_ROW,
      col,
      label,
      styles.header,
    );

    applyCellStyle(
      ws,
      DATE_ROW,
      col,
      styles.header,
    );
  });

  setMerge(
    ws,
    `${totalColumnLetter}4:${totalColumnLetter}5`,
  );

  setMerge(
    ws,
    `${signatureColumnLetter}4:${signatureColumnLetter}5`,
  );

  setCell(
    ws,
    WEEKDAY_ROW,
    TOTAL_COL,
    t('Monthly Total'),
    styles.header,
  );

  // One holiday column with two vertically stacked header cells.
  setCell(
    ws,
    WEEKDAY_ROW,
    HOLIDAY_COL,
    t('šv.val..'),
    styles.header,
  );

  setCell(
    ws,
    DATE_ROW,
    HOLIDAY_COL,
    t('D.D.'),
    styles.header,
  );

  setCell(
    ws,
    WEEKDAY_ROW,
    SIGNATURE_COL,
    t('Employee Signature'),
    styles.header,
  );

  applyCellStyle(
    ws,
    DATE_ROW,
    TOTAL_COL,
    styles.header,
  );

  applyCellStyle(
    ws,
    DATE_ROW,
    SIGNATURE_COL,
    styles.header,
  );

  // Weekday names and calendar dates.
  for (let day = 1; day <= daysInMonth; day += 1) {
    const col = FIRST_DAY_COL + day - 1;
    const date = monthStart.clone().date(day);

    const weekdayStyle = date.day() === 0 || date.day() === 6
      ? styles.weekend
      : styles.weekday;

    setCell(
      ws,
      WEEKDAY_ROW,
      col,
      resolvedWeekdayLabels[date.day()],
      weekdayStyle,
    );

    setCell(
      ws,
      DATE_ROW,
      col,
      day,
      styles.date,
    );
  }

  // Employee schedule rows.
  employees.forEach((employee, employeeIndex) => {
    const firstRow = FIRST_EMPLOYEE_ROW
      + employeeIndex * ROWS_PER_EMPLOYEE;

    const dayRow = firstRow + 1;
    const nightRow = firstRow + 2;
    const totalRow = firstRow + 3;

    const dayHours = Array(daysInMonth).fill(0);
    const nightHours = Array(daysInMonth).fill(0);
    const dailyTotals = Array(daysInMonth).fill(0);

    const shiftsByDay = Array.from(
      { length: daysInMonth },
      () => [],
    );

    // Use the supplied hour values; do not calculate hours
    // from shift start/end times.
    employee.events.forEach((event) => {
      if (!event || !event.start) return;

      const { start, text } = formatShift(
        event,
        employee.id,
      );

      if (
        start.year() !== resolved.year
        || start.month() !== resolved.month - 1
      ) {
        return;
      }

      const dayIndex = start.date() - 1;

      shiftsByDay[dayIndex].push(text);

      dayHours[dayIndex] += toHourNumber(
        event.worked_hours,
        'worked_hours',
        employee.id,
      );

      nightHours[dayIndex] += toHourNumber(
        event.night_hours,
        'night_hours',
        employee.id,
      );

      dailyTotals[dayIndex] += toHourNumber(
        event.total_hours,
        'total_hours',
        employee.id,
      );
    });

    const roundedDayHours = dayHours.map((value) => toHourNumber(value, 'worked_hours', employee.id));

    const roundedNightHours = nightHours.map((value) => toHourNumber(value, 'night_hours', employee.id));

    const roundedDailyTotals = dailyTotals.map((value) => toHourNumber(value, 'total_hours', employee.id));

    const monthDayTotal = toHourNumber(
      roundedDayHours.reduce((sum, value) => sum + value, 0),
      'worked_hours',
      employee.id,
    );

    const monthNightTotal = toHourNumber(
      roundedNightHours.reduce((sum, value) => sum + value, 0),
      'night_hours',
      employee.id,
    );

    const monthTotal = toHourNumber(
      roundedDailyTotals.reduce((sum, value) => sum + value, 0),
      'total_hours',
      employee.id,
    );

    const hourlyRate = toHourlyRate(
      employee.hourlyRate,
      employee.id,
    );

    // Employee details, hourly rate, holiday, and signature cells
    // span all four rows belonging to this employee.
    const employeeColumnStyles = [
      { col: 0, style: styles.name },
      { col: 1, style: styles.name },
      { col: 2, style: styles.name },
      { col: 3, style: styles.hourlyRate },
      { col: HOLIDAY_COL, style: styles.signature },
      { col: SIGNATURE_COL, style: styles.signature },
    ];

    employeeColumnStyles.forEach(({ col, style }) => {
      const colLetter = XLSX.utils.encode_col(col);

      setMerge(
        ws,
        `${colLetter}${firstRow}:${colLetter}${totalRow}`,
      );

      applyRangeStyle(ws, firstRow, totalRow, col, style);
    });

    setCell(ws, firstRow, 0, employeeIndex + 1, styles.name);
    setCell(ws, firstRow, 1, employee.name, styles.name);
    setCell(ws, firstRow, 2, employee.skill, styles.name);
    setCell(ws, firstRow, 3, hourlyRate, styles.hourlyRate);

    setCell(ws, firstRow, 4, t('Val.'), styles.label);
    setCell(ws, dayRow, 4, t('D'), styles.label);
    setCell(ws, nightRow, 4, t('N'), styles.label);
    setCell(ws, totalRow, 4, t('Total'), styles.totalLabel);

    for (let day = 1; day <= daysInMonth; day += 1) {
      const col = FIRST_DAY_COL + day - 1;
      const dayIndex = day - 1;

      setCell(
        ws,
        firstRow,
        col,
        shiftsByDay[dayIndex].join('\n'),
        styles.body,
      );

      setCountCell(
        ws,
        dayRow,
        col,
        roundedDayHours[dayIndex] > 0
          ? roundedDayHours[dayIndex]
          : '',
        styles.body,
      );

      setCountCell(
        ws,
        nightRow,
        col,
        roundedNightHours[dayIndex] > 0
          ? roundedNightHours[dayIndex]
          : '',
        styles.body,
      );

      setCountCell(
        ws,
        totalRow,
        col,
        roundedDailyTotals[dayIndex],
        styles.total,
      );
    }

    // Monthly totals are formatted strings, not Excel SUM formulas.
    setCell(ws, firstRow, TOTAL_COL, '', styles.body);

    setCountCell(
      ws,
      dayRow,
      TOTAL_COL,
      monthDayTotal,
      styles.body,
    );

    setCountCell(
      ws,
      nightRow,
      TOTAL_COL,
      monthNightTotal,
      styles.body,
    );

    setCountCell(
      ws,
      totalRow,
      TOTAL_COL,
      monthTotal,
      styles.totalMonth,
    );

    // Holiday and signature cells retain their borders without fills.
    applyRangeStyle(ws, firstRow, totalRow, HOLIDAY_COL, styles.signature);
    applyRangeStyle(ws, firstRow, totalRow, SIGNATURE_COL, styles.signature);
  });

  // Add a thicker right border after every Sunday.
  // Row 1 is a merged blank row, and row 2 contains the company/title bands.
  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = monthStart.clone().date(day);

    if (date.day() === 0) {
      const sundayCol = FIRST_DAY_COL + day - 1;

      for (let row = WEEKDAY_ROW; row <= lastRow; row += 1) {
        applyRightBorder(
          ws,
          row,
          sundayCol,
          SUNDAY_SEPARATOR_BORDER,
        );
      }
    }
  }

  // Bold vertical borders for column E and the last three columns.
  const BOLD_VERTICAL_COLS = [
    4, // E: Shift / Day / Night / Total
    TOTAL_COL,
    HOLIDAY_COL,
    SIGNATURE_COL,
  ];

  for (let row = WEEKDAY_ROW; row <= lastRow; row += 1) {
    BOLD_VERTICAL_COLS.forEach((col) => {
      applyBorderSides(
        ws,
        row,
        col,
        ['left', 'right'],
        SECTION_BORDER,
      );
    });
  }

  applyRowBorder(ws, DATE_ROW, 0, LAST_COL, ['bottom'], SECTION_BORDER);

  // Bold horizontal border after each employee's 4-row block.
  employees.forEach((_, employeeIndex) => {
    const lastEmployeeRow = FIRST_EMPLOYEE_ROW
      + employeeIndex * ROWS_PER_EMPLOYEE
      + ROWS_PER_EMPLOYEE
      - 1;

    applyRowBorder(ws, lastEmployeeRow, 0, LAST_COL, ['bottom'], SECTION_BORDER);
  });

  // Optional full-width description row below the table.
  // Keep its text horizontally centered and retain the outer border.
  if (hasDescription) {
    const descriptionStyle = {
      ...styles.description,
      alignment: {
        ...styles.description.alignment,
        horizontal: 'center',
        vertical: 'center',
        wrapText: true,
      },
    };

    setMerge(
      ws,
      `A${descriptionRow}:${lastColumnLetter}${descriptionRow}`,
    );

    for (let col = 0; col <= LAST_COL; col += 1) {
      applyCellStyle(
        ws,
        descriptionRow,
        col,
        descriptionStyle,
      );
    }

    setCell(
      ws,
      descriptionRow,
      0,
      String(description),
      descriptionStyle,
    );

    lastRow += 1;
  }

  // Add a thick outline around the main schedule table (A4 to lastRow).
  applyRowBorder(ws, WEEKDAY_ROW, 0, LAST_COL, ['top'], TABLE_OUTLINE_BORDER);
  applyRowBorder(ws, lastRow, 0, LAST_COL, ['bottom'], TABLE_OUTLINE_BORDER);

  for (let row = WEEKDAY_ROW; row <= lastRow; row += 1) {
    applyBorderSides(
      ws,
      row,
      0,
      ['left'],
      TABLE_OUTLINE_BORDER,
    );

    applyBorderSides(
      ws,
      row,
      LAST_COL,
      ['right'],
      TABLE_OUTLINE_BORDER,
    );
  }

  // Column widths.
  ws['!cols'] = [
    { wch: 6 }, // A: employee number
    { wch: 17 }, // B: employee name
    { wch: 17 }, // C: position
    { wch: 14 }, // D: hourly rate
    { wch: 7 }, // E: Shift / Day / Night / Total
    ...Array.from(
      { length: daysInMonth },
      () => ({ wch: 9 }),
    ),
    { wch: 11 }, // Monthly Total
    { wch: 11 }, // Holiday hours
    { wch: 22 }, // Employee Signature
  ];

  

  // Auto-fit columns.
  [0,1,2,3,4].forEach(colIndex => autoFitColumn(ws, colIndex));

  autoFitColumn(ws, TOTAL_COL, { minWidth: 2, maxWidth: 40, padding: 0 })
  autoFitColumn(ws, HOLIDAY_COL);
  autoFitColumn(ws, SIGNATURE_COL, { minWidth: 2, maxWidth: 40, padding: 0 })

  // Row heights.
  ws['!rows'] = [];
  ws['!rows'][0] = { hpt: 28 };
  ws['!rows'][1] = { hpt: 25 };
  ws['!rows'][2] = { hpt: 22 };
  ws['!rows'][3] = { hpt: 30 };
  ws['!rows'][4] = { hpt: 22 };

  if (hasDescription) {
    ws['!rows'][descriptionRow - 1] = { hpt: 30 };
  }

  employees.forEach((employee, employeeIndex) => {
    const firstRow = FIRST_EMPLOYEE_ROW
      + employeeIndex * ROWS_PER_EMPLOYEE;

    ws['!rows'][firstRow - 1] = { hpt: 30 };
    ws['!rows'][firstRow] = { hpt: 19 };
    ws['!rows'][firstRow + 1] = { hpt: 19 };
    ws['!rows'][firstRow + 2] = { hpt: 20 };
  });

  ws['!freeze'] = {
    xSplit: FIRST_DAY_COL,
    ySplit: DATE_ROW,
    topLeftCell: 'F6',
    activePane: 'bottomRight',
    state: 'frozen',
  };

  ws['!ref'] = `A1:${lastColumnLetter}${Math.max(finalRow, DATE_ROW)}`;

  ws['!pageSetup'] = {
    orientation: 'landscape',
    fitToWidth: 1,
    fitToHeight: 0,
  };

  ws['!margins'] = {
    left: 0.25,
    right: 0.25,
    top: 0.5,
    bottom: 0.5,
    header: 0.2,
    footer: 0.2,
  };

  return {
    workbook: wb,
    sheetName,
    employeeCount: employees.length,
    year: resolved.year,
    month: resolved.month,
    daysInMonth,
    descriptionRow,
  };
}
